import type { CCAMActe } from "cadendar-shared";
import { CCAMActeCard } from "./CCAMActeCard";
import { CCAMSearchEmptyState } from "./CCAMSearchEmptyState";
import { useInfiniteScroll } from "./hooks/useInfiniteScroll";

export interface CCAMSearchResultsProps {
  results: CCAMActe[];
  onSelectActe: (acte: CCAMActe) => void;
  isLoading: boolean;
  query: string;
  hasMore?: boolean;
  onLoadMore?: () => void;
}

/**
 * Grille de résultats de recherche CCAM avec infinite scroll
 * Story 2.4: Grid responsive (3 cols desktop, 2 tablet, 1 mobile) + infinite scroll
 */
export const CCAMSearchResults = ({
  results,
  onSelectActe,
  isLoading,
  query,
  hasMore = false,
  onLoadMore = () => {},
}: CCAMSearchResultsProps) => {
  const sentinelRef = useInfiniteScroll(onLoadMore, hasMore, isLoading);

  const getActeRenderKey = (acte: CCAMActe, index: number) => {
    // Code CCAM can repeat across versions/imports, so build a composite key.
    return [
      acte.code,
      acte.dateEffet ?? "",
      acte.dateFin ?? "",
      acte.acteType ?? "",
      index,
    ].join("::");
  };

  // Loading state
  if (isLoading && results.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
        <span className="ml-3 text-gray-600">Recherche en cours...</span>
      </div>
    );
  }

  // Empty state
  if (!isLoading && results.length === 0 && query.length > 0) {
    return <CCAMSearchEmptyState query={query} />;
  }

  // Results count indicator
  const resultsCount = results.length;

  return (
    <div>
      {/* Results count */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="mb-4 text-sm text-gray-600"
      >
        {resultsCount} résultat{resultsCount > 1 ? "s" : ""}
      </div>

      {/* Responsive grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {results.map((acte, index) => (
          <CCAMActeCard
            key={getActeRenderKey(acte, index)}
            acte={acte}
            onSelect={onSelectActe}
          />
        ))}
      </div>

      {/* Infinite scroll sentinel */}
      {hasMore && (
        <div
          ref={sentinelRef}
          data-testid="infinite-scroll-sentinel"
          className="flex items-center justify-center py-8"
        >
          {isLoading ? (
            <div className="flex items-center">
              <div className="animate-spin h-6 w-6 border-3 border-blue-500 border-t-transparent rounded-full" />
              <span className="ml-3 text-gray-600">Chargement...</span>
            </div>
          ) : (
            <span className="text-gray-400 text-sm">Scroll pour plus</span>
          )}
        </div>
      )}
    </div>
  );
};
